import { ReorderReviewDialog } from "@/components/reorder/ReorderReviewDialog";
import { ProductThumbnailImage } from "@/components/product/ProductThumbnailImage";
import { FavoriteHeart } from "@/components/favorites/FavoriteHeart";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useReportLoadFailure } from "@/hooks/useReportLoadFailure";
import { analytics } from "@/lib/analytics";
import { handleActionError, RETRY_HINT } from "@/lib/errors";
import {
  isAddable,
  normalizeResolvedItems,
  reasonLabel,
  summarize,
  summarizeSelection,
  type LineSelection,
  type ReorderLine
} from "@/lib/reorder-diff";
import {
  useAddListToCart,
  useDeleteSavedList,
  usePreviewSavedList,
  useRemoveSavedListItem,
  useRenameSavedList,
  useSavedList,
  useUpdateSavedListItem
} from "@/queries/customer/saved-lists";
import { useFavoritesListId } from "@/queries/customer/favorites";
import { useRegions } from "@/queries/regions";
import { convertToLocale } from "@/utils/money";
import type { SalonStockerHttpTypes } from "@repo/api-sdk";
import { toast } from "@medusajs/ui";
import { TruncateTooltip } from "@repo/design-system";
import { useNavigate } from "@tanstack/react-router";
import {
  ArrowLeft,
  Minus,
  Pencil,
  Plus,
  ShoppingCart,
  Trash2
} from "lucide-react";
import { memo, useCallback, useMemo, useState } from "react";

import { SavedByLabel } from "./SavedByLabel";
import { SavedListNameDialog } from "./SavedListNameDialog";

type ResolvedItem = SalonStockerHttpTypes.StoreResolvedSavedListItem;

interface SavedListDetailViewProps {
  listId: string;
  onBack: () => void;
  /** Called after the list is deleted (return to the index). */
  onDeleted: () => void;
}

/**
 * Saved List detail (F3, #59). Rows come from the dry-run **preview** (live
 * price + availability, product title/thumbnail resolved server-side).
 * Unavailable lines are flagged, never auto-removed. Add-to-cart reuses the F2
 * `ReorderReviewDialog`. Never shows shipping/freight - a list is not a cart.
 */
export const SavedListDetailView = memo(function SavedListDetailView({
  listId,
  onBack,
  onDeleted
}: SavedListDetailViewProps) {
  const list = useSavedList(listId);
  const preview = usePreviewSavedList(listId);
  useReportLoadFailure(preview.error, "saved list detail");
  const regions = useRegions();

  const currencyCode = regions.data?.regions?.[0]?.currency_code ?? "aud";

  const updateItem = useUpdateSavedListItem();
  const removeItem = useRemoveSavedListItem();
  const renameList = useRenameSavedList();
  const deleteList = useDeleteSavedList();
  const addToCart = useAddListToCart();
  const navigate = useNavigate();

  const [renameOpen, setRenameOpen] = useState(false);
  const [renameError, setRenameError] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  /** The (seller_id:variant_id) key of a line whose mutation is in flight. */
  const [pendingKey, setPendingKey] = useState<string | null>(null);

  const items = preview.data?.items ?? [];
  /** The saved-list items normalized into the shared reorder-diff vocabulary. */
  const lines = useMemo(() => normalizeResolvedItems(items), [items]);

  const fmt = useCallback(
    (amount: number) =>
      convertToLocale({ amount, currency_code: currencyCode }),
    [currencyCode]
  );

  const subtotal = useMemo(
    () =>
      lines.reduce(
        (sum, line) =>
          isAddable(line) ? sum + (line.unit_price ?? 0) * line.quantity : sum,
        0
      ),
    [lines]
  );
  const addableCount = useMemo(() => lines.filter(isAddable).length, [lines]);

  const setQuantity = useCallback(
    async (item: ResolvedItem, quantity: number) => {
      const key = `${item.seller_id}:${item.variant_id}`;
      setPendingKey(key);
      try {
        if (quantity <= 0) {
          await removeItem.mutateAsync({
            listId,
            key: { variant_id: item.variant_id, seller_id: item.seller_id }
          });
        } else {
          await updateItem.mutateAsync({
            listId,
            item: {
              variant_id: item.variant_id,
              seller_id: item.seller_id,
              quantity
            }
          });
        }
        analytics.savedListItemQuantityChanged({
          list_id: listId,
          quantity: Math.max(0, quantity)
        });
      } catch (err) {
        toast.error("Could not update the list", {
          description: handleActionError(err, {
            boundary: "saved list update item",
            fallback: RETRY_HINT
          })
        });
      } finally {
        setPendingKey(null);
      }
    },
    [listId, removeItem, updateItem]
  );

  const handleRename = useCallback(
    async (name: string) => {
      setRenameError(null);
      try {
        await renameList.mutateAsync({ listId, name });
        setRenameOpen(false);
      } catch (err) {
        setRenameError(
          handleActionError(err, {
            boundary: "saved list rename",
            fallback: "We couldn't rename your list. Please try again."
          })
        );
      }
    },
    [listId, renameList]
  );

  const handleDelete = useCallback(async () => {
    try {
      await deleteList.mutateAsync(listId);
      setDeleteOpen(false);
      onDeleted();
    } catch (err) {
      toast.error("Could not delete the list", {
        description: handleActionError(err, {
          boundary: "saved list delete",
          fallback: RETRY_HINT
        })
      });
    }
  }, [listId, deleteList, onDeleted]);

  const handleConfirmAddToCart = useCallback(
    async (selections: LineSelection[]) => {
      setAddError(null);
      try {
        const result = await addToCart.mutateAsync({ listId, selections });
        const counts = summarize(normalizeResolvedItems(result.items));

        // Compare what the buyer submitted against the resolved preview to
        // capture the "why" (ADR-0003/0004).
        const selected = new Set(selections.map((s) => s.variant_id));
        const quantities = Object.fromEntries(
          selections.map((s) => [s.variant_id, s.quantity])
        );
        const selectionCounts = summarizeSelection(lines, selected, quantities);

        analytics.savedListAddedToCart({
          list_id: listId,
          added_count: counts.added,
          unavailable_count: counts.unavailable,
          cart_count: result.carts?.length ?? 0,
          deselected_count: selectionCounts.deselectedCount,
          quantity_overridden_count: selectionCounts.overriddenCount
        });
        setReviewOpen(false);
        if (counts.added > 0) {
          toast.success(
            `Added ${counts.added} ${counts.added === 1 ? "item" : "items"} to your cart`
          );
          navigate({ to: "/cart" });
        } else {
          toast.info("Nothing on this list is available to add right now");
        }
      } catch (err) {
        setAddError(
          handleActionError(err, {
            boundary: "saved list add to cart",
            fallback:
              "We couldn't add this list to your cart. Please try again."
          })
        );
      }
    },
    [listId, lines, addToCart, navigate]
  );

  const name = list.data?.saved_list.name ?? "Saved list";
  const isBusy = preview.isLoading || list.isLoading;
  const favoritesListId = useFavoritesListId();
  const isFavoritesList = listId === favoritesListId;

  return (
    <div>
      <header className="border-border border-b p-6">
        <button
          type="button"
          onClick={onBack}
          className="mb-3 inline-flex items-center gap-1 text-sm font-medium text-purple-600 hover:text-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-600 dark:text-purple-400"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          All lists
        </button>

        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex min-w-0 items-center gap-2">
              {isFavoritesList && <FavoriteHeart className="h-5 w-5" />}
              <TruncateTooltip
                as="h2"
                className="min-w-0 text-xl font-semibold text-gray-900 dark:text-white"
              >
                {name}
              </TruncateTooltip>
            </div>
            {list.data?.saved_list.created_by ? (
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                <SavedByLabel label={list.data.saved_list.created_by.label} />
              </p>
            ) : null}
          </div>

          <div className="flex flex-shrink-0 gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setRenameOpen(true)}
            >
              <Pencil className="mr-2 h-4 w-4" aria-hidden="true" />
              Rename
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setDeleteOpen(true)}
              aria-label="Delete list"
            >
              <Trash2 className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        </div>
      </header>

      <section className="p-6">
        {isBusy ? (
          <ul className="space-y-3" aria-hidden="true">
            {[0, 1, 2].map((i) => (
              <li key={i} className="flex gap-3">
                <Skeleton className="h-12 w-12 rounded-md" />
                <div className="flex-1">
                  <Skeleton className="mb-2 h-4 w-48" />
                  <Skeleton className="h-4 w-24" />
                </div>
              </li>
            ))}
          </ul>
        ) : preview.error ? (
          <div
            role="alert"
            className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-400"
          >
            We couldn&apos;t resolve this list. Please try again.
          </div>
        ) : items.length === 0 ? (
          <div
            className="flex min-h-[200px] flex-col items-center justify-center rounded-lg border border-dashed border-gray-300 p-6 text-center dark:border-gray-700"
            role="status"
          >
            <p className="text-lg font-medium text-gray-900 dark:text-white">
              This list is empty
            </p>
            <p className="mt-1 max-w-sm text-sm text-gray-500 dark:text-gray-400">
              Browse products and use &quot;Add to list&quot; to build it up, or
              save a whole cart as a new list.
            </p>
          </div>
        ) : (
          <>
            <ul className="divide-border divide-y">
              {items.map((item, index) => (
                <SavedListRow
                  key={`${item.seller_id}:${item.variant_id}`}
                  item={item}
                  line={lines[index]}
                  fmt={fmt}
                  pending={
                    pendingKey === `${item.seller_id}:${item.variant_id}`
                  }
                  onSetQuantity={setQuantity}
                />
              ))}
            </ul>

            <div className="border-border mt-6 flex items-center justify-between border-t pt-4">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Subtotal (available items)
                </p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {fmt(subtotal)}
                </p>
                <p className="text-xs text-gray-400">
                  Shipping is calculated per supplier at checkout.
                </p>
              </div>
              <Button
                type="button"
                onClick={() => {
                  setAddError(null);
                  setReviewOpen(true);
                }}
                disabled={addableCount === 0}
              >
                <ShoppingCart className="mr-2 h-4 w-4" aria-hidden="true" />
                Add to cart
              </Button>
            </div>
          </>
        )}
      </section>

      <ReorderReviewDialog
        open={reviewOpen}
        onOpenChange={setReviewOpen}
        title="Add list to your cart"
        lines={lines}
        currencyCode={currencyCode}
        isConfirming={addToCart.isPending}
        error={addError}
        onConfirm={handleConfirmAddToCart}
      />

      <SavedListNameDialog
        open={renameOpen}
        onOpenChange={setRenameOpen}
        title="Rename list"
        submitLabel="Save name"
        defaultName={name}
        isSubmitting={renameList.isPending}
        error={renameError}
        onSubmit={handleRename}
      />

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete this list?</DialogTitle>
            <DialogDescription>
              &quot;{name}&quot; will be permanently removed. This can&apos;t be
              undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeleteOpen(false)}
              disabled={deleteList.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteList.isPending}
            >
              {deleteList.isPending ? "Deleting…" : "Delete list"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
});

const SavedListRow = memo(function SavedListRow({
  item,
  line,
  fmt,
  pending,
  onSetQuantity
}: {
  /** The raw list item - carries the `(seller_id, variant_id)` mutation key. */
  item: ResolvedItem;
  /** Its resolved classification in the shared reorder-diff vocabulary. */
  line: ReorderLine;
  fmt: (amount: number) => string;
  pending: boolean;
  onSetQuantity: (item: ResolvedItem, quantity: number) => void;
}) {
  const unavailable = !isAddable(line);

  return (
    <li className="flex gap-3 py-3">
      <div className="h-12 w-12 flex-shrink-0 overflow-hidden rounded-md bg-gray-100 dark:bg-neutral-800">
        <ProductThumbnailImage
          src={item.thumbnail ?? null}
          alt=""
          className={
            unavailable
              ? "h-full w-full object-cover opacity-50 grayscale"
              : "h-full w-full object-cover"
          }
          fallback={null}
        />
      </div>

      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-start justify-between gap-3">
          <TruncateTooltip
            as="p"
            className={
              unavailable
                ? "text-sm font-medium text-gray-400 line-through dark:text-gray-500"
                : "text-sm font-medium text-gray-900 dark:text-white"
            }
          >
            {item.title || "Product"}
          </TruncateTooltip>
          {!unavailable ? (
            <span className="flex-shrink-0 text-sm font-semibold text-gray-900 dark:text-white">
              {fmt((item.unit_price ?? 0) * item.quantity)}
            </span>
          ) : null}
        </div>

        <div className="mt-2 flex items-center justify-between gap-3">
          <div className="border-border inline-flex items-center rounded-md border">
            <button
              type="button"
              className="flex h-8 w-8 items-center justify-center text-gray-600 hover:bg-gray-100 disabled:opacity-40 dark:text-gray-300 dark:hover:bg-neutral-800"
              onClick={() => onSetQuantity(item, item.quantity - 1)}
              disabled={pending}
              aria-label="Decrease quantity"
            >
              <Minus className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
            <span className="w-8 text-center text-sm tabular-nums">
              {item.quantity}
            </span>
            <button
              type="button"
              className="flex h-8 w-8 items-center justify-center text-gray-600 hover:bg-gray-100 disabled:opacity-40 dark:text-gray-300 dark:hover:bg-neutral-800"
              onClick={() => onSetQuantity(item, item.quantity + 1)}
              disabled={pending}
              aria-label="Increase quantity"
            >
              <Plus className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          </div>

          <div className="flex items-center gap-3">
            {unavailable ? (
              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500 dark:bg-neutral-800 dark:text-gray-400">
                {reasonLabel(line.reason)}
              </span>
            ) : null}
            <button
              type="button"
              className="text-gray-400 hover:text-red-600 disabled:opacity-40 dark:hover:text-red-400"
              onClick={() => onSetQuantity(item, 0)}
              disabled={pending}
              aria-label={`Remove ${item.title || "item"}`}
            >
              <Trash2 className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </li>
  );
});
